import apiClient from './client';
import { Alert, Pagination, ApiResponse } from '@/types';

/**
 * Alert query parameters
 */
export interface AlertQueryParams {
  page?: number;
  limit?: number;
  severity?: string;
  type?: string;
  acknowledged?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Alert list response
 */
export interface AlertListResponse {
  data: Alert[];
  pagination: Pagination;
}

/**
 * Alert statistics
 * Counts cover all alerts (same scope as the unfiltered list) unless `days` is
 * passed; `unacknowledged`/`unresolved` are always all-time.
 */
export interface AlertStats {
  total: number;
  bySeverity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
    info: number;
  };
  byType?: Record<string, number>;
  unacknowledged: number;
  unresolved?: number;
}

/**
 * Alert stats query parameters (same filters as the list)
 */
export interface AlertStatsParams {
  days?: number;
  severity?: string;
  type?: string;
  acknowledged?: boolean;
  resolved?: boolean;
}

/**
 * Ensure an alert has a string `id`, falling back to Mongo's `_id`
 */
export function normalizeAlert(alert: Alert): Alert {
  const raw = alert as Alert & { _id?: string };
  const id = raw.id || (raw._id !== undefined && raw._id !== null ? String(raw._id) : '');
  return id === raw.id ? alert : { ...alert, id };
}

/**
 * Get the id of an alert, tolerating payloads that only carry `_id`
 */
export function getAlertId(alert: Alert): string | undefined {
  return normalizeAlert(alert).id || undefined;
}

/**
 * Get alerts with filters
 */
export async function getAlerts(params?: AlertQueryParams): Promise<AlertListResponse> {
  const response = await apiClient.get<ApiResponse<AlertListResponse>>('/alerts', { params });
  const result = response.data.data as AlertListResponse;
  return {
    ...result,
    data: (result?.data || []).map(normalizeAlert),
  };
}

/**
 * Get alert statistics
 */
export async function getAlertStats(params?: AlertStatsParams): Promise<AlertStats> {
  const response = await apiClient.get<ApiResponse<AlertStats>>('/alerts/stats', { params });
  return response.data.data as AlertStats;
}

/**
 * Acknowledge an alert
 */
export async function acknowledgeAlert(id: string): Promise<Alert> {
  if (!id) {
    throw new Error('Cannot acknowledge an alert without an id');
  }
  const response = await apiClient.patch<ApiResponse<Alert>>(`/alerts/${encodeURIComponent(id)}/acknowledge`);
  return normalizeAlert(response.data.data as Alert);
}

/**
 * Resolve an alert
 */
export async function resolveAlert(id: string): Promise<Alert> {
  if (!id) {
    throw new Error('Cannot resolve an alert without an id');
  }
  const response = await apiClient.patch<ApiResponse<Alert>>(`/alerts/${encodeURIComponent(id)}/resolve`);
  return normalizeAlert(response.data.data as Alert);
}

/**
 * Acknowledge multiple alerts
 */
export async function acknowledgeAlerts(ids: string[]): Promise<void> {
  const validIds = ids.filter(Boolean);
  if (validIds.length === 0) return;
  await apiClient.post('/alerts/acknowledge-bulk', { ids: validIds });
}

/**
 * Acknowledge every unacknowledged alert (not just the current page)
 */
export async function acknowledgeAllAlerts(): Promise<{ modifiedCount: number }> {
  const response = await apiClient.post<ApiResponse<{ modifiedCount: number }>>('/alerts/acknowledge-all');
  return response.data.data as { modifiedCount: number };
}
